import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';

import { AuthService } from '../../account/services/auth.service';
import { StorageService } from '../../account/services/storage.service';
import {
  CurrentUser,
  CurrentUserService,
} from '../../account/services/current-user.service';
import {
  AppLanguage,
  LanguageService,
} from '../../portal/shared/services/language.service';
import { FeatureFlagService } from '../../portal/shared/services/feature-flag.service';
import { NotificationService } from '../../portal/modules/registration-application/services/notification/notification.service';
import {
  AdminMenuItem,
  buildAdminMenuItems,
  buildProfileMenuItems,
  PortalNavItem,
} from '../../portal/portal-navigation.constants';
import { AIFC_ENTRY_PATH, AifcNavItem, buildAifcNav } from '../navigation/aifc-navigation.constants';
import { aifcPortalUrl } from '../navigation/aifc-portal-url';

/**
 * Shared AIFC top bar, ported from aifc-portal's `vex-toolbar` + `vex-navigation`
 * pair. Used by the public landing and by the portal shell so both read as one
 * site. Host pages can inject extra actions (chat, notifications) through the
 * `[headerActions]` content slot.
 */
@Component({
  selector: 'app-aifc-header',
  templateUrl: './aifc-header.component.html',
  styleUrls: ['./aifc-header.component.scss'],
  standalone: false,
})
export class AifcHeaderComponent implements OnInit, OnDestroy {
  /**
   * Разделы в выпадающем меню профиля — то же боковое меню портала. Считается
   * один раз в поле, а не геттером: `@Input` и биндинги сравниваются по ссылке,
   * а новый массив на каждый цикл change detection не даёт ей сойтись.
   * Флаги приезжают в `APP_INITIALIZER`, то есть до создания компонента.
   */
  public readonly userMenuItems: PortalNavItem[];

  /**
   * Личные кабинеты администраторов aifc-portal, отфильтрованные по ролям
   * текущего пользователя. Тот же принцип, что у `userMenuItems`: считается
   * один раз в поле, роли приезжают в sessionStorage до создания компонента.
   */
  public readonly adminMenuItems: AdminMenuItem[];

  /**
   * Entries rendered directly in the pill, flag-filtered once at construction
   * via `buildAifcNav` (flags arrive in `APP_INITIALIZER`, before the component
   * exists). Computed in the ctor rather than a field initializer so the
   * injected `FeatureFlagService` is available — and once, not per change-
   * detection cycle, so the `*ngFor` reference stays stable.
   */
  public readonly navItems: AifcNavItem[];
  /** The single "Marketplaces" entry rendered as a dropdown after them. */
  public readonly navDropdown: AifcNavItem | undefined;

  public currentLang: AppLanguage;
  public currentUser: CurrentUser | null = null;
  public isMobileMenuOpen = false;
  /**
   * Счётчик для колокольчика. Сокет поднимает `PortalHomeComponent`
   * (`notificationService.connect()`) — там гарантированно есть сессия; шапка
   * не открывает второе соединение и не дёргает сокет на публичном лендинге у
   * анонимного посетителя. Начальное значение тем не менее нужно и вне
   * портала (лендинг для залогиненного пользователя), поэтому шапка сама
   * подтягивает его REST-запросом (`notificationCount()`); дальнейшие
   * обновления, если сокет всё же поднят соседним компонентом, приходят через
   * `updateCount$`.
   */
  public notificationCount = 0;
  /** Accordion state of "Marketplaces" in the mobile menu — collapsed by default, like aifc-portal's `vex-sidenav-item`. */
  public isMobileDropdownOpen = false;

  private readonly subs = new Subscription();

  constructor(
    private router: Router,
    private authService: AuthService,
    private currentUserService: CurrentUserService,
    private languageService: LanguageService,
    private storageService: StorageService,
    private notificationService: NotificationService,
    private featureFlags: FeatureFlagService
  ) {
    this.currentLang = this.languageService.currentLang;
    this.userMenuItems = buildProfileMenuItems(featureFlags);
    this.adminMenuItems = buildAdminMenuItems(storageService, featureFlags);

    const aifcNav = buildAifcNav(featureFlags);
    this.navItems = aifcNav.filter((i) => !i.children);
    this.navDropdown = aifcNav.find((i) => !!i.children);
  }

  public ngOnInit(): void {
    this.subs.add(
      this.languageService.currentLang$.subscribe((lang) => {
        this.currentLang = lang;
      })
    );
    this.subs.add(
      this.currentUserService.currentUser$.subscribe((user) => {
        this.currentUser = user;
      })
    );

    this.notificationCount = this.notificationService.count;
    this.subs.add(
      this.notificationService.updateCount$.subscribe((count) => {
        this.notificationCount = count;
      })
    );

    if (this.showNotifications) {
      this.subs.add(
        this.notificationService.notificationCount().subscribe((count) => {
          this.notificationService.count = count;
          this.notificationCount = count;
          this.notificationService.updateCount$.next(count);
        })
      );
    }
  }

  public ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  public get languages(): AppLanguage[] {
    return this.languageService.languages;
  }

  public get isAuthenticated(): boolean {
    return this.currentUser !== null;
  }

  /** Колокольчик — только залогиненным и только при включённом флаге. */
  public get showNotifications(): boolean {
    return this.isAuthenticated && this.featureFlags.isEnabled('EXT_NOTIFICATIONS');
  }

  /** Display name shown in the account dropdown header. */
  public get userName(): string {
    return this.currentUser?.name ?? '';
  }

  public isActive(item: AifcNavItem): boolean {
    return this.matchesRoute(item.routerLink);
  }

  /** True when any child of the "Marketplaces" dropdown matches the current URL. */
  public get isDropdownActive(): boolean {
    return !!this.navDropdown?.children?.some((child) => this.matchesRoute(child.routerLink));
  }

  private matchesRoute(routerLink: string | undefined): boolean {
    if (!routerLink) {
      return false;
    }
    const url = this.router.url.split(/[?#]/)[0];
    return routerLink === '/' ? url === '/' : url === routerLink || url.startsWith(`${routerLink}/`);
  }

  public toggleMobileMenu(): void {
    this.isMobileMenuOpen = !this.isMobileMenuOpen;
    if (!this.isMobileMenuOpen) {
      this.isMobileDropdownOpen = false;
    }
  }

  public toggleMobileDropdown(): void {
    this.isMobileDropdownOpen = !this.isMobileDropdownOpen;
  }

  public navigate(item: AifcNavItem): void {
    this.isMobileMenuOpen = false;
    this.isMobileDropdownOpen = false;

    if (item.routerLink) {
      this.router.navigateByUrl(item.routerLink);
      return;
    }
    if (item.externalPath) {
      window.location.href = this.resolveExternalUrl(item);
    }
  }

  public setLang(lang: AppLanguage): void {
    this.languageService.use(lang);
  }

  public login(): void {
    // Resolves to the Keycloak-hosted page or /account/login depending on
    // environment.authCodeFlow — AuthService owns that decision.
    this.authService.startLoginRedirect(this.router.url);
  }

  public logout(): void {
    this.subs.add(
      this.authService.logout().subscribe(() => {
        // In Authorization Code mode Keycloak performs its own redirect; this
        // only matters for the in-app password flow.
        if (!this.authService.isAuthCodeFlow) {
          this.router.navigateByUrl('/');
        }
      })
    );
  }

  public goTo(route: string): void {
    this.isMobileMenuOpen = false;
    this.router.navigateByUrl(route);
  }

  /** Пункт меню профиля, ведущий в раздел aifc-portal. */
  public goToExternal(path: string): void {
    this.isMobileMenuOpen = false;
    window.location.href = aifcPortalUrl(path);
  }

  /**
   * Resolves the browser target for an AIFC-portal nav item.
   *
   * The "Home" entry is the cross-portal landing. Its `/dashboard` is guarded,
   * so we branch on the visitor's auth state: an authenticated afsa user goes
   * straight to `/dashboard` (SSO carries the session across), while an
   * anonymous visitor goes to the public `/home` instead of being bounced to
   * the Keycloak login page. URLs are built through `aifcPortalUrl`, which is
   * path-style (the portal has no hash strategy); every other AIFC item passes
   * its `externalPath` straight through.
   *
   * The cross-portal SSO hint (`?sso=1`) is not added here: `aifcPortalUrl`
   * attaches it centrally whenever the visitor is authenticated, so it rides
   * along on both the `/dashboard` target and every other AIFC link, and never
   * on the anonymous `/home` fallback.
   */
  private resolveExternalUrl(item: AifcNavItem): string {
    if (item.externalPath === AIFC_ENTRY_PATH) {
      return aifcPortalUrl(this.isAuthenticated ? '/dashboard' : '/home');
    }
    return aifcPortalUrl(item.externalPath!);
  }
}
